/**
 * Salary payout method + CliQ destination validation.
 *
 * CliQ is JoPACC's instant-transfer rail: a salary can be sent to a Jordanian
 * mobile number, a bank-registered alias, or an IBAN. The backend normalizes
 * and validates the destination on write and only ever reads back a masked
 * form, so the same grammar has to exist here — the profile form pre-validates
 * with it and the mocks reject with it, which is what keeps mock mode honest
 * about what the server accepts.
 *
 * Per project conventions Testing Philosophy this is MUST-test tier: a
 * data transform on a money-destination boundary.
 */

import { isValidIban } from "./bank-file-errors";

export const PAYOUT_METHODS = [
  "BANK_TRANSFER",
  "CASH",
  "CLIQ",
  "CHEQUE",
  "MONEY_TRANSFER",
] as const;
export type PayoutMethod = (typeof PAYOUT_METHODS)[number];

export const CLIQ_ALIAS_TYPES = ["MOBILE", "ALIAS", "IBAN"] as const;
export type CliqAliasType = (typeof CLIQ_ALIAS_TYPES)[number];

/**
 * Jordanian mobile in any of the five forms the server accepts, reduced to the
 * stored canonical `+9627XXXXXXXX`. Spaces, dashes and parentheses are stripped
 * first — people paste numbers as they read them. Returns null when the input
 * is not a Jordanian mobile at all.
 */
export function normalizeCliqMobile(raw: string): string | null {
  const digits = raw.replace(/[\s\-()]/g, "");
  const match = /^(?:\+962|00962|962|0)?(7\d{8})$/.exec(digits);
  return match === null ? null : `+962${match[1]}`;
}

export function isValidCliqMobile(raw: string): boolean {
  return normalizeCliqMobile(raw) !== null;
}

/** A bank-registered CliQ alias: 2–50 characters once trimmed. */
export function isValidCliqAlias(raw: string): boolean {
  const len = raw.trim().length;
  return len >= 2 && len <= 50;
}

/**
 * The canonical value the server would store for this destination, or null
 * when the value is invalid for the chosen type.
 */
export function normalizeCliqValue(type: CliqAliasType, raw: string): string | null {
  if (type === "MOBILE") return normalizeCliqMobile(raw);
  if (type === "ALIAS") return isValidCliqAlias(raw) ? raw.trim() : null;
  const iban = raw.replace(/\s+/g, "").toUpperCase();
  return isValidIban(iban) ? iban : null;
}

/** The read-back form: four stars plus the last four characters of the canonical value. */
export function maskCliqValue(canonical: string): string {
  return `****${canonical.slice(-4)}`;
}

/**
 * Display label for a payout method, catalog-first. The tenant catalog is the
 * authority on wording (and on which methods exist at all); `fallback` covers
 * the catalog being unavailable or not carrying the enum. A null/undefined
 * method is the legacy-unset record, which the backend treats as BANK_TRANSFER.
 */
export function payoutMethodLabel(
  method: PayoutMethod | null | undefined,
  catalog: ReadonlyArray<{ method: string; labelEn: string; labelAr: string }> | undefined,
  lang: "ar" | "en",
  fallback: string,
): string {
  const resolved = method ?? "BANK_TRANSFER";
  const entry = catalog?.find((item) => item.method === resolved);
  if (entry === undefined) return fallback;
  return lang === "ar" ? entry.labelAr : entry.labelEn;
}
